import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { getBusType } from '../../VehicleModelHelper';
import styles from './CompatibilityTable.module.scss';
import PidLabel from '../PidLabel';
import VehicleInfo from './VehicleInfo';
import { useSelector } from 'react-redux';
import VehicleCompatibilityDetailModal from '../../VehicleCompatibilityDetailModal';
import { FeatureMeasurementTable, getStatusConfig } from '../../constant';


const transformDataForTable = (vehicleCompatibility) => {
  const tableData = [];

  vehicleCompatibility.forEach((vehicle, vehicleIndex) => {
    const validGroupModels = vehicle.groupModel.filter(groupModel => !!groupModel.vins.length);

    const allSourceAndBusTypePairs = [];

    validGroupModels.forEach(groupModel => {
      [...groupModel.vins].sort((a, b) => Number(a?.vinDetail?.canId) - Number(b?.vinDetail?.canId)).forEach(busType => {
        allSourceAndBusTypePairs.push({
          id: groupModel.id,
          source: groupModel.source,
          busType: busType.vinDetail.vin,
          canId: busType.vinDetail?.canId,
          attachments: groupModel.attachments,
          supportedPid: groupModel.supportedPid,
        })
      })
    });

    validGroupModels.forEach((groupModel, groupIndex) => {
      [... groupModel.vins].sort((a, b) => Number(a?.vinDetail?.canId) - Number(b?.vinDetail?.canId)).forEach((busType, busTypeIndex) => {
        const firstOfVehicleModel = groupIndex === 0 && busTypeIndex === 0;
        tableData.push({
          id: groupModel.id,
          key: `${vehicleIndex}-${groupIndex}-${busTypeIndex}`,
          vehicleIndex,
          vehicleKey: `${vehicle.make}-${vehicle.model}-${vehicle.yearOfManufacture}`,
          make: vehicle.make,
          model: vehicle.model,
          yearList: vehicle.yearList,
          source: groupModel.displaySource,
          priority: groupModel.priority,
          busType: busType.vinDetail.vin,
          canId: busType.vinDetail?.canId,
          pids: [...busType.modelPids],
          supportedDataPoolIdentifier: groupModel.supportedDataPoolIdentifier,
          markSupportedKey: vehicle.supportedPid.filter(i=>i.verificationStatus !== "N" && i.verificationStatus !== "X" && (groupModel.source?.toLowerCase() !== "internal" || i.verificationStatus !== "U")).map(i=>i.dataPoolIdentifier),
          pidVerificationStatus: groupModel.pidVerificationStatus,
          isDummyInternal: groupModel.isDummyInternal,
          rowSpan: firstOfVehicleModel ? vehicle.groupModel.reduce((sum, { vins }) => sum + vins.length, 0) : 0,
          fullGroupModels: firstOfVehicleModel ? vehicle.fullGroupModels : [],
          allSourceAndBusTypePairs: allSourceAndBusTypePairs
        })
      })
    })
  })

  return tableData;
}

const MatrixTable = ({
  vehicleCompatibility,
  dataPoolIdentifiers,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedVehicleCompatibility, setSelectedVehicleCompatibility] = useState();
  const [vehicleCompatibilityDetailModal, setVehicleCompatibilityDetailModal] = useState(false);
  const [paginatedVehicleModels, setPaginatedVehicleModels] = useState([]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  useEffect(()=>{
    setPaginatedVehicleModels(vehicleCompatibility.slice(startIndex, endIndex))
  }, [startIndex, endIndex, vehicleCompatibility]);

  const tableData = useMemo(() => transformDataForTable(paginatedVehicleModels), [
    paginatedVehicleModels
  ]);
  const category = useSelector(state => state.vehicleModelFilter.category);
  const searchObj = useSelector(state => state.vehicleModelFilter.searchFilter);

  const handleShowMore = (vehicleModelIndex) => {
    setSelectedVehicleCompatibility(paginatedVehicleModels[vehicleModelIndex]);
    setVehicleCompatibilityDetailModal(true);
  }

  const fixedColumns = useMemo(() => [
    {
      title: t('VehicleModel.VehicleInfo'),
      fixed: 'left',
      dataIndex: 'vehicleInfo',
      key: 'vehicleInfo',
      width: 184,
      colSpan: 3,
      onCell: (record) => ({ rowSpan: record.rowSpan }),
      render: (value, record, index) => <VehicleInfo record={record} onSelectVehicleModel={handleShowMore} />
    },
    {
      title: '',
      dataIndex: 'source',
      key: 'source',
      fixed: 'left',
      rowSpan: 0,
      width: 80,
      render: (value, record, index) => {return <div style={{"whiteSpace":"nowrap"}}>{value +" - "+record.priority}</div>}
    },
    {
      title: '',
      dataIndex: 'busTypeWithCan',
      key: 'busTypeWithCan',
      fixed: 'left',
      rowSpan: 0,
      width: 120,
      className: styles.breakWord,
      render: (value, record, index) => getBusType(record),
    }
  ], [paginatedVehicleModels]);

  const columns = useMemo(() => {
    const combinedColumns = [...fixedColumns];

    if (category === 'feature') {
      const statusConfig = getStatusConfig();
      combinedColumns.push({
        key: 'feature',
        title: t('VehicleModel.MeasurementCategory.feature').toUpperCase(),
        children: Object.entries(FeatureMeasurementTable).map(([key, identifierGroup]) => {
          const getSupportedStatus = (model) => identifierGroup.every(group => group.some(identifier => model.markSupportedKey.includes(identifier)));
          return {
            key,
            minWidth: 130,
            title: t('VehicleModel.FeatureMeasurement.' + key),
            align: 'center',
            render: (_, record) => {
              const isSupported = getSupportedStatus(record);
              const featureStatus = isSupported ? statusConfig.VERIFIED : statusConfig.UNKNOWN;
              const Icon = featureStatus.icon;

              return (
                <div style={{ display: 'inline-block' }}>
                  <Icon style={{ color: featureStatus.color, fontSize: '22px', lineHeight: '22px' }} />
                </div>
              )
            }
          }
        })
      })
    } else {
      const identifierGroup = dataPoolIdentifiers[category];
      combinedColumns.push({
        key: category,
        title: (
          <Tooltip title={t('VehicleModel.MeasurementCategory.' + category + 'Tip')}>
            {t('VehicleModel.MeasurementCategory.' + category).toUpperCase()}
          </Tooltip>
        ),
        children: Object.entries(identifierGroup)
          .filter(i=>(searchObj?.measurements || []).length === 0 || (searchObj?.measurements || []).includes(i[0]))
          .map(([key, description]) => {
            return ({
              key,
              minWidth: 130,
              title: description,
              align: 'center',
              render: (_, record) =>
                <div style={{ display: 'inline-block' }}>
                  <PidLabel
                    modelPids={record.pids}
                    dataPoolIdentifier={{ key, value: description }}
                    showLabel={false}
                    vin={record.busType}
                    model={record}
                  />
                </div>
            })
          }),
      })
    }

    return combinedColumns;
  }, [category, fixedColumns, dataPoolIdentifiers, searchObj?.measurements, t]);

  return (
    <div>
      <Table
        sticky
        className={styles.matrixTable}
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        rowClassName={(record) => {
          // Apply alternating colors based on vehicle index
          return record.vehicleIndex % 2 === 0 ? styles.vehicleEven : styles.vehicleOdd;
        }}
      />
      <Pagination
        className={styles.pagination}
        align="end"
        current={currentPage}
        pageSize={pageSize}
        total={vehicleCompatibility.length} // Total vehicles, not rows
        onChange={(page, pageSize) => {
          setCurrentPage(page);
          setPageSize(pageSize);
        }}
      />
      <VehicleCompatibilityDetailModal
        vehicleDetail={selectedVehicleCompatibility}
        isModalOpen={vehicleCompatibilityDetailModal}
        onClose={() => {
          setSelectedVehicleCompatibility(undefined);
          setVehicleCompatibilityDetailModal(false);
        }}
      />
    </div>
  )
};

export default MatrixTable;